import _ from 'underscore';
import { useContext } from 'react';
import {
  makeAbsoluteUrl,
  useHttpCoreFetch,
} from '@bingads-webui-campaign-react/http-core';

import { CampaignFetchContext } from './use-campaign-fetch-provider';
import type {
  CampaignBuiltResponseError,
  CampaignFetchHookOptions,
  CampaignFetchResponseLike,
  CampaignFetchResult,
  CampaignResponseErrorInput,
} from './types';

class FetchError extends Error {
  constructor(errors: CampaignBuiltResponseError[]) {
    super('An CMAPI fetch error occurred');
    (this as unknown as FetchError & { error: CampaignBuiltResponseError[] }).error = errors;
  }
}


export function buildResponseErrors(responseErrors: CampaignResponseErrorInput[] | null | undefined): CampaignBuiltResponseError[] {
  const errors: CampaignBuiltResponseError[] = [];
  _.each(responseErrors, (err) => {
    if (!_.isEmpty(err && err.Message)) {
      errors.push({
        Code: err.Code,
        Message: err.Message,
        Details: err.Details,
        ErrorCode: err.ErrorCode,
      });
    }
  });
  return errors;
}

export function throwOnFetchError(responseErrors: CampaignResponseErrorInput[] | unknown): void {
  if (_.isArray(responseErrors) && !_.isEmpty(responseErrors)) {
    const errors = buildResponseErrors(responseErrors);
    throw new FetchError(errors);
  }
}

function getData<TResponse>(response: TResponse): TResponse | null {
  if (response == null) {
    return null;
  }

  const responseWithErrors = response as TResponse & { OperationErrors?: CampaignResponseErrorInput[] };
  throwOnFetchError(responseWithErrors.OperationErrors);

  return response;
}

export function useCampaignFetch<TData = unknown>(
  urlOverride?: string,
  optionsOverride?: CampaignFetchHookOptions,
  dependencies?: readonly unknown[]
): CampaignFetchResult<TData> {
  const context = useContext(CampaignFetchContext);
  const url = urlOverride || makeAbsoluteUrl(context.url || '');
  const options = optionsOverride || context.options;
  return useHttpCoreFetch<TData, CampaignFetchResponseLike>({
    baseUrl: url,
    requestOptions: options,
    dependencies,
    transformResponse: getData,
  });
}
